import { useEffect, useState } from "react";
import NextLink from "next/link";
import PropTypes from "prop-types";
import throttle from "lodash.throttle";
import useDetectScrollDirection from "hooks/useDetectScrollDirection";
import { useLanguage } from "contexts/LocalizationContext";
import { useModal } from "contexts/ModalContext";
import { useMobileMenu } from "contexts/MobileMenuContext";
import FlagBox from "components/LanguageSwitcher/FlagBox";
import { HamburgerMenuIcon } from "styles/icons";
import { useFlagBoxSettings } from "contexts/SettingsContext";
import TopBanner from "modules/common/components/TopBanner";
import { Chakra, Logos } from "@airhelp/react-ui-components";
import MobileMenuContent from "./MobileMenuContent";
import { TOP_MENU_LANG_MODAL_OPEN_GTM, TOP_MENU_LANG_MODAL_OPEN_MP } from "../../trackingConsts";

const { Box, Flex, Slide, useBreakpointValue, Button, Link } = Chakra;

const HeaderNav = ({ logo: Logo, menuItems, headerButtons, topBannerText, topBannerButtonText, subMenu }) => {
  const [slideOpen, setSlideOpen] = useState(true);
  const [smallNav, setSmallNav] = useState(false);

  const language = useLanguage();
  const { openModal } = useModal();
  const { openMobileMenu } = useMobileMenu();
  const hideFlagBox = useFlagBoxSettings();

  const scrollDirection = useDetectScrollDirection("80");

  const navBoxShadow = smallNav ? "1px 4px 10px rgba(203, 202, 209, 0.25)" : "none";
  const logoSmallWidth = "95px";
  const logoLargeWidth = "118px";
  const logoWidth = { base: logoSmallWidth, lg: smallNav ? logoSmallWidth : logoLargeWidth };

  const topBannerComponent = topBannerText && topBannerButtonText && (
    <TopBanner bannerText={topBannerText} buttonText={topBannerButtonText} />
  );

  const navOnScroll = () => {
    setSmallNav(window.pageYOffset > 0);
  };

  const throttledResizeListener = throttle(navOnScroll, 50);

  useEffect(() => {
    setSlideOpen(scrollDirection !== "down");

    window.addEventListener("scroll", throttledResizeListener);

    return () => window.removeEventListener("scroll", throttledResizeListener);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollDirection]);

  return (
    <Slide
      initial="visible"
      direction="top"
      in={useBreakpointValue({ base: slideOpen, lg: true })}
      style={{ zIndex: 4 }}
    >
      {topBannerComponent}
      <Box
        align="center"
        justify="space-between"
        wrap="wrap"
        px={{ base: 4, xl: 16 }}
        color="greyscale.600"
        backgroundColor="white"
        py={4}
        width="100%"
        transition="0.1s"
        boxShadow={navBoxShadow}
      >
        <Flex as="nav" role="menubar" alignItems="center" justifyContent="space-between" maxWidth="1202px">
          <NextLink href={`/${language}/`} passHref>
            <Link>
              <Logo
                minW={logoWidth}
                transition="0.2s"
                minH={{ base: "35px", lg: "40px" }}
                title="AirHelp logo"
                alt="AirHelp logo"
              />
            </Link>
          </NextLink>

          <Box
            display={{ base: "none", lg: "flex" }}
            width={{ base: "full", md: "auto" }}
            justifyContent="flex-end"
            alignItems="center"
            flexGrow={1}
          >
            {menuItems}
          </Box>

          {!hideFlagBox && (
            <Box ml={{ base: "auto", lg: 9 }} mr={{ base: 3, lg: 0 }}>
              <FlagBox
                openModal={openModal}
                trackingEvents={{ gtmEvent: TOP_MENU_LANG_MODAL_OPEN_GTM, mixpanelEvent: TOP_MENU_LANG_MODAL_OPEN_MP }}
                dataQA="top-navbar-lang-switcher"
              />
            </Box>
          )}

          <Box display={{ base: "none", lg: "flex" }} mt={{ base: 4, md: 0 }}>
            {headerButtons}
          </Box>

          {subMenu && <Box d={{ base: "block", md: "none" }}>{subMenu}</Box>}

          <Button
            variant="unstyled"
            _hover={{}}
            _active={{}}
            display={{ base: "flex", lg: "none" }}
            onClick={() => openMobileMenu(<MobileMenuContent headerButtons={headerButtons} menuItems={menuItems} />)}
            data-testid="mobile-menu-button"
            aria-label="Open site menu"
          >
            <HamburgerMenuIcon color="primary.900" w={6} h={4} />
          </Button>
        </Flex>
      </Box>
    </Slide>
  );
};

export default HeaderNav;

HeaderNav.propTypes = {
  logo: PropTypes.elementType,
  menuItems: PropTypes.node,
  headerButtons: PropTypes.node,
  topBannerText: PropTypes.string,
  topBannerButtonText: PropTypes.string,
  subMenu: PropTypes.node,
};

HeaderNav.defaultProps = {
  logo: Logos.Airhelp,
  menuItems: "",
  headerButtons: "",
  topBannerText: "",
  topBannerButtonText: "",
  subMenu: false,
};
